/**
 * @fileoverview Plan badge component.
 * @summary Displays a small circular subscription plan badge for header overlay.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/PlanBadge
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { SubscriptionType } from '@karaonow/shared'
import { getPlanBadgeLabel, getPlanBadgeColor } from '../../../utils/planUtils'
import type { FC } from 'react'

interface PlanBadgeProps {
  /**
   * The subscription plan to display
   */
  planType: SubscriptionType | string
  /**
   * Optional CSS class for positioning/sizing
   */
  className?: string
  /**
   * Optional click handler (makes badge clickable)
   */
  onClick?: () => void
}

/**
 * @summary Displays a small circular subscription plan badge.
 * Designed to be overlaid on the header logo (8x8 or 10x10).
 *
 * @param {PlanBadgeProps} props - Component props
 * @returns {ReactElement} Plan badge component
 */
const PlanBadge: FC<PlanBadgeProps> = ({ planType, className = '', onClick }) => {
  const normalizedPlanType = (planType as SubscriptionType) || SubscriptionType.FREE
  const label = getPlanBadgeLabel(normalizedPlanType)
  const bgColor = getPlanBadgeColor(normalizedPlanType)

  return (
    <button
      onClick={onClick}
      type="button"
      className={`absolute inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-[10px] font-bold ${bgColor} shadow-lg border border-white transition-transform hover:scale-110 ${onClick ? 'cursor-pointer' : ''} ${className}`}
      title={`Plan: ${label}${onClick ? ' (Cliquez pour voir plus)' : ''}`}
    >
      {label}
    </button>
  )
}

export default PlanBadge

